import styles from "@/styles/shop-info/earn-crazy-points.module.scss";
import Link from "next/link";
import Image from "next/image";
import { UpOutlined } from "@ant-design/icons";

import getConfig from "next/config";
const { publicRuntimeConfig } = getConfig();
const imageDomain = publicRuntimeConfig.imageDomain;

interface CompEarnCrazyPointsContentProps {
  isInModal?: boolean;
  modalRef?: React.RefObject<HTMLElement>;
}

export default function CompEarnCrazyPointsContent({
  isInModal = false,
  modalRef,
}: CompEarnCrazyPointsContentProps) {
  // 回到顶部
  const backToTop = () => {
    if (isInModal && modalRef?.current) {
      // 如果在弹窗中，滚动到弹窗的顶部
      modalRef.current.scrollTo({
        top: 0,
        behavior: "smooth",
      });
    } else {
      // 如果在页面中，滚动到页面顶部
      window.scrollTo({
        top: 0,
        behavior: "smooth", // 添加平滑滚动效果
      });
    }
  };

  interface QuestionItem {
    question: string;
    answer: React.ReactNode;
  }

  const questionList = [
    {
      question: `What are CrazyPoints?`,
      answer: (
        <div className={styles["answer-desc"]}>
          As a customer at CrazySales.com.au, every dollar you spend (not
          including shipping costs) will earn you 1 CrazyPoint. CrazyPoints are
          bonus points that save you even more on future purchases! At checkout,
          simply redeem the points you have earned for further discounts. Earn
          enough points and get items for FREE!
          <br />
          Currently 1,000 CrazyPoints is equal to AUD $10.
          <br />
          <br />
          (Your CrazyPoints) x 0.010 = AUD$ Value
        </div>
      ),
    },
    {
      question: "How do I earn CrazyPoints?",
      answer: (
        <>
          <div className={styles["answer-desc"]}>
            {`1) `}CrazyPoints are credited to your account automatically for
            every successfully completed transaction. If you&apos;ve ordered and
            paid for anything on CrazySales, you&apos;ve already earned
            CrazyPoints.
          </div>
          <Image
            className={styles["content-img"]}
            style={{ display: "block", margin: "0 auto" }}
            src={`${imageDomain}/faq/crazypoint-pic2.png`}
            alt={"crazy-points-banner"}
            width={800}
            height={139}
          ></Image>
          <div className={styles["answer-desc"]} style={{ margin: "10px 0" }}>
            {`2) After receving your product, you can also write a review to earn 100-400 CrazyPoints.`}
          </div>
          <Image
            className={styles["content-img"]}
            style={{ display: "block", margin: "0 auto" }}
            src={`${imageDomain}/faq/crazypoint-pic4.png`}
            alt={"crazy-points-banner"}
            width={800}
            height={139}
          ></Image>
        </>
      ),
    },
    {
      question: "How do I redeem CrazyPoints?",
      answer: (
        <>
          <div className={styles["answer-desc"]}>
            You can use CrazyPoints to purchase almost anything you want in our
            store. Simply redeem the CrazyPoints during the checkout process in
            order to get a further discount. Or, if you have enough CrazyPoints,
            you will be able to get the items you want for FREE!
          </div>
          <Image
            className={styles["content-img"]}
            style={{ display: "block", margin: "0 auto" }}
            src={`${imageDomain}/faq/crazypoint-pic3.png`}
            alt={"crazy-points-banner"}
            width={800}
            height={139}
          ></Image>
        </>
      ),
    },
    {
      question: "Do CrazyPoints expire?",
      answer: (
        <div className={styles["answer-desc"]}>
          Your unused CrazyPoints expire in 24 months.
        </div>
      ),
    },
    {
      question: `What's the dollar value of 1,000 CrazyPoints?`,
      answer: (
        <div className={styles["answer-desc"]}>
          Currently, 1,000 CrazyPoints is worth AUD $10 at CrazySales.com.au. To
          calculate what your current CrazyPoints are worth, multiply them by
          0.010.
        </div>
      ),
    },
    {
      question: `Is there a limit to how many CrazyPoints I can earn?`,
      answer: (
        <div className={styles["answer-desc"]}>
          No! Every member can earn unlimited CrazyPoints!
        </div>
      ),
    },
    {
      question: "Can I transfer my CrazyPoints to another person?",
      answer: (
        <div className={styles["answer-desc"]}>
          Unfortunately the points are not directly transferable, but you can
          use your CrazyPoints towards one of our Gift Vouchers.
        </div>
      ),
    },
    {
      question: "Will I earn CrazyPoints for my delivery and handling fees?",
      answer: (
        <div className={styles["answer-desc"]}>
          Sorry, your delivery and handling fees are excluded when calculating
          the amount of CrazyPoints you&apos;ll earn.
        </div>
      ),
    },
    {
      question: `What happens to my CrazyPoints if I have an item refunded?`,
      answer: (
        <div className={styles["answer-desc"]}>
          If an item is refunded, the CrazyPoints you&apos;d earned by
          purchasing that product will be deducted from your account. If an item
          is purchased by using CrazyPoints in conjunction with any other kind
          of payment, we will refund the money and CrazyPoints you have used.
          <br />
          e.g.
          <br />
          An item is $13 + $3 (Delivery and Handling Fee) = Total Amount of $16.
          <br />
          You have used 1000 CrazyPoints (Value = $10) to get $10 discount, so
          total amount now is $6. Then you paid $6 to purchase this item.
          <br />
          You have earned 3 CrazyPoints. (As Delivery and Handling Fee is
          excluded when calculating the amount of points earned)
          <br />
          To refund this item, you will have 3 CrazyPoints deducted from your
          account, but will have $6 refunded plus 1000 CrazyPoints back to you.
          <br />
          For other question(s) which isn&apos;t listed above, please email our
          customer service team at &nbsp;
          <Link href="mailto:info@crazysales.com.au" legacyBehavior>
            <a rel="noopener noreferrer" className={styles["link"]}>
              info@crazysales.com.au
            </a>
          </Link>
          &nbsp; — our customer service team will be happy to assist you.
          <br />
        </div>
      ),
    },
  ];

  return (
    <div className={styles["comp-earn-crazy-points"]}>
      <Image
        className={styles["banner-img"]}
        style={{ display: "block", margin: "0 auto" }}
        src={`${imageDomain}/legal/earn-crazy-points-banner.png`}
        alt={"crazy-points-banner"}
        width={800}
        height={139}
      ></Image>
      <div className={styles["content-title"]}>
        Earn CrazyPoints with our Reward Program!
      </div>
      <div className={`${styles["answer-desc"]}`}>
        At CrazySales, the more you buy, the more you save! With our CrazyPoints
        reward program, we reward our customers&apos; loyalty. Every dollar you
        spend at CrazySales earns you a CrazyPoint, and the more CrazyPoints you
        earn, the bigger the discount you&apos;ll receive on your future
        CrazySales purchases.
      </div>
      <div className={`${styles["answer-desc"]} padding-top-10`}>
        All you need to do is sign in and buy at CrazySales. That&apos;s it. You
        don&apos;t need to sign up and pay for any special programs: just
        complete a transaction and the points will automatically be added to
        your account. You can save your accumulated CrazyPoints for up to two
        years before spending them, and you can spend them on any applicable
        product on CrazySales.
      </div>
      <Image
        className={styles["banner-img"]}
        style={{ display: "block", margin: "0 auto" }}
        src={`${imageDomain}/legal/earn-crazy-points-pic-1.png`}
        alt={"crazy-points-banner"}
        width={800}
        height={139}
      ></Image>

      <div className={styles["questions-box"]}>
        {questionList.map((item: QuestionItem, index: number) => (
          <div key={`faq-${index}`}>
            <div className={styles["title"]} id={`faq${index}`}>
              Q{index + 1}. {item.question}
            </div>
            <div className={styles["answer"]}>{item.answer}</div>
            <div className={styles["back-top"]}>
              <UpOutlined
                style={{ color: "#D70B17", marginRight: "4px" }}
                onClick={backToTop}
              />
              <span onClick={backToTop}>Back to Top</span>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
